import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { isEmpty } from "lodash";
import * as yup from "yup";
import { useFormik } from "formik";
import { Grid2, Typography, TextField, Button } from "@mui/material";
import { PAGE_LIST } from "../index";
import { selectLoggedIn } from "../../store/userSlice/userSelectors";
import { getQueryObject } from "../../helpers/urlBuilder";
import { resetPassword } from "../../store/userSlice/userRequests";
import GeneralHeader from "../../components/GeneralHeader";
import Footer from "../../components/Footer";
import Layout from "../Layout";
import { addAlert } from "../../store/alertSlice/alertSlice";

import classes from "./index.module.scss";

const ResetPasswordPage = () => {
  const isLoggedIn = useSelector(selectLoggedIn);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const query = getQueryObject();
  const code = query.code;

  useEffect(() => {
    if (isLoggedIn) {
      navigate(PAGE_LIST.CERTIFICATE);
    }

    if (!code) {
      navigate(PAGE_LIST.LOGIN);
    }
  }, [isLoggedIn, code]);

  const validationSchema = yup.object({
    password: yup.string()
      .required('Password is required')
      .test('min-length', 'Password should contain at least 8 characters', (val = '') => val.length >= 8)
      .test('max-length', 'Password is too long', (val = '') => val.length <= 64)
      .test('lowercaseUppercase', 'Password should be a mixture of lower case and upper case letters, numbers and special characters (#, ?, !)',
        (val = '') => /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{8,}$/.test(val)),
    confirmPassword: yup.string().required('Please enter the same password')
      .oneOf([yup.ref('password'), null], 'Please enter the same password'),
  });

  const resetFormik = useFormik({
    initialValues: {
      code,
      password: '',
      confirmPassword: '',
    },
    validationSchema,
    onSubmit: () => {},
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
  });

  const onSubmit = async () => {
    const errorList = await resetFormik.validateForm();

    if (isEmpty(errorList)) {
      if (isEmpty(errorList)) {
        dispatch(resetPassword(resetFormik.values))
          .then((action) => {
            const errorText = action?.payload?.message;

            if (errorText) {
              resetFormik.setErrors({
                password: true,
                confirmPassword: errorText
              })
            } else {
              dispatch(addAlert({
                text: 'Password has been reset successfully',
                severity: 'success',
              }))
            }
          })
      }
    }
  }

  return (
    <Layout
      header={<GeneralHeader />}
      center={(
        <Grid2
          container
          direction="column"
          className={classes.container}
          spacing={2}
        >
          <Grid2 size={12} className={classes.width}>
            <Typography variant='h1' align='center'>Reset your password</Typography>
          </Grid2>
          <Grid2 size={12} className={classes.width}>
            <Typography variant='body1'>Password</Typography>
            <TextField
              variant='outlined'
              name='password'
              size='medium'
              type="password"
              fullWidth
              value={resetFormik.values?.password}
              onChange={resetFormik.handleChange}
              error={Boolean(resetFormik?.errors?.password)}
              helperText={resetFormik?.errors?.password}
            />
          </Grid2>
          <Grid2 size={12} className={classes.width}>
            <Typography variant='body1'>Confirm your password</Typography>
            <TextField
              variant='outlined'
              name='confirmPassword'
              size='medium'
              type="password"
              fullWidth
              value={resetFormik.values?.confirmPassword}
              onChange={resetFormik.handleChange}
              error={Boolean(resetFormik?.errors?.confirmPassword)}
              helperText={resetFormik?.errors?.confirmPassword}
            />
          </Grid2>
          <Grid2 size={12} className={classes.width}>
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={onSubmit}
            >
              Reset
            </Button>
          </Grid2>
        </Grid2>
      )}
      footer={<Footer />}
    />
  );
}

export default ResetPasswordPage;